import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    API_GET_CLIENTS, API_UPDATE_CLIENT_STATUS
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';

const ClientManagement = () => {
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [search, setSearch] = useState({
        client_name: '',
        center_name: '',
        center_no: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const clientsPerPage = 10;

    const navigate = useNavigate();
    const { logout } = useAuth();

    const getAllClients = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_GET_CLIENTS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ is_all_list: true })
            });

            if (response.status === 401 || response.status === 403) {
                logout();
                return;
            }

            const responseData = await response.json();
            setClients(responseData.data);
            setFilteredClients(responseData.data);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    useEffect(() => {
        getAllClients();
    }, []);

    useEffect(() => {
        const filtered = clients.filter(client =>
            client.client_name?.toLowerCase().includes(search.client_name.toLowerCase()) &&
            client.center_name?.toLowerCase().includes(search.center_name.toLowerCase()) &&
            client.center_no?.toLowerCase().includes(search.center_no.toLowerCase())
        );
        setFilteredClients(filtered);
    }, [clients, search]);


    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        const updatedSearch = { ...search, [name]: value };
        setSearch(updatedSearch);

        const filtered = clients.filter(client =>
            client.client_name?.toLowerCase().includes(updatedSearch.client_name.toLowerCase()) &&
            client.center_name?.toLowerCase().includes(updatedSearch.center_name.toLowerCase()) &&
            client.center_no?.toLowerCase().includes(updatedSearch.center_no.toLowerCase())
        );
        setFilteredClients(filtered);
        setCurrentPage(1); // Reset to first page on filter
    };

    const toggleClientStatus = async (clientId) => {
        try {
            const client = clients.find(c => c.id === clientId);
            const newStatus = client.is_active === 1 ? 0 : 1;
            const token = localStorage.getItem('token');

            const response = await fetch(API_UPDATE_CLIENT_STATUS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ client_id: clientId, is_active: newStatus }),
            });

            if (response.status === 401 || response.status === 403) {
                logout();
                return;
            }

            const result = await response.json();

            if (result.data.affectedRows) {
                setClients(prev =>
                    prev.map(c =>
                        c.id === clientId ? { ...c, is_active: newStatus } : c
                    )
                );
            } else {
                alert('Failed to update client status');
            }
        } catch (error) {
            console.error('Error updating client status:', error);
            alert('An error occurred while updating the client status.');
        }
    };

    // Pagination logic
    const indexOfLastClient = currentPage * clientsPerPage;
    const indexOfFirstClient = indexOfLastClient - clientsPerPage;
    const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
    const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="container my-5">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <h2 className="mb-2 mb-md-0 float-left">Client List</h2>
                <button onClick={() => navigate('/client-details')} className="btn btn-primary float-right width200">
                    Add New Client
                </button>
            </div>

            <div className="table-responsive">
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Center No</th>
                            <th>Center Name</th>
                            <th>Client Name</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                        <tr>
                            <th></th>
                            <th>
                                <input
                                    type="text"
                                    name="center_no"
                                    value={search.center_no}
                                    onChange={handleSearchChange}
                                    className="form-control form-control-sm"
                                    placeholder="Search Center No"
                                />
                            </th>
                            <th>
                                <input
                                    type="text"
                                    name="center_name"
                                    value={search.center_name}
                                    onChange={handleSearchChange}
                                    className="form-control form-control-sm"
                                    placeholder="Search Center Name"
                                />
                            </th>
                            <th>
                                <input
                                    type="text"
                                    name="client_name"
                                    value={search.client_name}
                                    onChange={handleSearchChange}
                                    className="form-control form-control-sm"
                                    placeholder="Search Client Name"
                                />
                            </th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentClients.length > 0 ? (
                            currentClients.map((client, index) => (
                                <tr key={client.id}>
                                    <td>{indexOfFirstClient + index + 1}</td>
                                    <td>{client.center_no}</td>
                                    <td>{client.center_name}</td>
                                    <td>{client.client_name}</td>
                                    <td>{client.is_active === 1 ? 'Active' : 'Inactive'}</td>
                                    <td>
                                        <button
                                            className={`btn btn-sm ${client.is_active === 1 ? 'btn-danger' : 'btn-success'}`}
                                            onClick={() => toggleClientStatus(client.id)}
                                        >
                                            {client.is_active === 1 ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center">No clients found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <nav>
                <ul className="pagination justify-content-center">
                    {Array.from({ length: totalPages }, (_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button onClick={() => handlePageChange(i + 1)} className="page-link">
                                {i + 1}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>
        </div>
    );
};

export default ClientManagement;
